// File: frontend/src/pages/dashboards/TeacherDashboard.jsx

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/axios';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        await Promise.all([
          api.get('/api/v1/attendance/summary').catch(() => ({ data: null })),
          api.get('/api/v1/exams').catch(() => ({ data: null })),
          api.get('/api/v1/students', { params: { limit: 1 } }).catch(() => ({ data: null }))
        ]);
      } catch (error) {
        console.error('Error loading teacher dashboard:', error);
      }
    };

    fetchTeacherData();
  }, []);

  const teacherName = user?.name || user?.email || 'Teacher';

  return (
    <DashboardLayout>
      {/* Sub Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Teacher Portal</h1>
          <p className="text-xs text-purple-600 font-semibold mt-0.5">
            Dashboard <span className="text-slate-400">/</span> Class Management & Grading
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate('/ai-tests')}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl shadow-md transition flex items-center space-x-1"
          >
            <span>✨</span>
            <span>Generate Test</span>
          </button>
          <button
            onClick={() => navigate('/attendance/mark')}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-xl shadow-purple-glow transition"
          >
            📅 Mark Attendance
          </button>
        </div>
      </div>

      {/* Welcome Banner */}
      <div className="bg-purple-gradient rounded-3xl p-6 sm:p-8 text-white shadow-xl">
        <span className="px-3 py-1 bg-white/10 text-purple-200 rounded-full text-[10px] font-extrabold uppercase tracking-wider border border-white/10">
          TEACHING FACULTY
        </span>
        <h2 className="text-2xl sm:text-3xl font-extrabold mt-2">
          Welcome, {teacherName}! 📚
        </h2>
        <p className="text-purple-200 text-xs sm:text-sm mt-1">
          Manage your assigned classes, log attendance, enter exam marks, and check class timetables.
        </p>
      </div>

      {/* Quick Action Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Link
          to="/attendance/mark"
          className="p-5 rounded-2xl bg-white border border-purple-100 shadow-purple-subtle hover:shadow-md transition text-center group"
        >
          <div className="text-3xl mb-2">📅</div>
          <span className="font-bold text-slate-800 group-hover:text-purple-700 text-xs block">Mark Attendance</span>
          <span className="text-[10px] text-slate-400 block mt-0.5">Daily class roster</span>
        </Link>

        <Link
          to="/exams"
          className="p-5 rounded-2xl bg-white border border-purple-100 shadow-purple-subtle hover:shadow-md transition text-center group"
        >
          <div className="text-3xl mb-2">📝</div>
          <span className="font-bold text-slate-800 group-hover:text-purple-700 text-xs block">Enter Marks</span>
          <span className="text-[10px] text-slate-400 block mt-0.5">Grades & Results</span>
        </Link>

        <Link
          to="/my-timetable"
          className="p-5 rounded-2xl bg-white border border-purple-100 shadow-purple-subtle hover:shadow-md transition text-center group"
        >
          <div className="text-3xl mb-2">🗓️</div>
          <span className="font-bold text-slate-800 group-hover:text-purple-700 text-xs block">My Timetable</span>
          <span className="text-[10px] text-slate-400 block mt-0.5">Class schedule</span>
        </Link>

        <Link
          to="/students"
          className="p-5 rounded-2xl bg-white border border-purple-100 shadow-purple-subtle hover:shadow-md transition text-center group"
        >
          <div className="text-3xl mb-2">👨‍🎓</div>
          <span className="font-bold text-slate-800 group-hover:text-purple-700 text-xs block">Student List</span>
          <span className="text-[10px] text-slate-400 block mt-0.5">Roster & info</span>
        </Link>
      </div>

      {/* Classes List */}
      <div className="bg-white rounded-3xl p-6 border border-purple-100 shadow-purple-subtle space-y-4">
        <h3 className="text-base font-bold text-slate-900 border-b border-purple-100 pb-3">My Assigned Classes</h3>
        <div className="space-y-3">
          <div className="p-4 rounded-2xl bg-purple-50/50 border border-purple-100 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-slate-900 text-sm">Class 10 - Section A</h4>
              <p className="text-xs text-slate-500">Subject: Mathematics & Computer Science</p>
            </div>
            <button
              onClick={() => navigate('/students')}
              className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition shadow"
            >
              View Roster
            </button>
          </div>

          <div className="p-4 rounded-2xl bg-purple-50/50 border border-purple-100 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-slate-900 text-sm">Class 9 - Section B</h4>
              <p className="text-xs text-slate-500">Subject: Physics & General Science</p>
            </div>
            <button
              onClick={() => navigate('/students')}
              className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition shadow"
            >
              View Roster
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TeacherDashboard;
